import { Alert, Box, Grid, Group, Stack, Text, Tooltip, UnstyledButton, useMantineTheme } from '@mantine/core'
import { IconAlertCircle, IconCheck, IconLock, IconStarFilled } from '@tabler/icons-react'
import React from 'react'

import avatars from '@/assets/avatars'
import { theme } from '@/configs/theme'
import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import type { SidekickOption } from '@/screens/SetupScreen/useSidekicks'

interface SidekickSelectionGridProps {
  options: SidekickOption[]
  selectedSidekicks: SidekickOption[]
  defaultSidekick: string | null
  onSidekickToggle: (sidekick: SidekickOption) => void
  onDefaultToggle: (sidekick: SidekickOption, e: React.MouseEvent) => void
  minSelections: number
  maxSelections: number
}

export function SidekickSelectionGrid({
  options,
  selectedSidekicks,
  defaultSidekick,
  onSidekickToggle,
  onDefaultToggle,
  minSelections,
  maxSelections,
}: SidekickSelectionGridProps) {
  const selectedCount = selectedSidekicks.length
  const isMinSelected = selectedCount >= minSelections
  const mantineTheme = useMantineTheme()
  const isMobile = useCheckMobileScreen()

  return (
    <>
      <Stack mt="xs" mb="xs" gap="xs">
        {!isMinSelected && (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light" py="xs">
            {`You must select at least ${minSelections} Personal Sidekick${minSelections === 1 ? '' : 's'}`}
          </Alert>
        )}
        {selectedCount > maxSelections && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" py="xs">
            {`You must select at most ${maxSelections} Personal Sidekick${maxSelections === 1 ? '' : 's'}`}
          </Alert>
        )}
        {isMinSelected && defaultSidekick === null && (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light" py="xs">
            You must select one member sidekick as the default
          </Alert>
        )}
        {isMinSelected && defaultSidekick !== null && (
          <div style={{ height: '36px', width: '100%', marginTop: '8px' }} />
        )}
      </Stack>

      <Grid gap="md">
        {options.map(option => {
          const isSelected = selectedSidekicks.some(sidekick => sidekick.id === option.id)
          const isDefault = defaultSidekick === option.id
          return (
            <Grid.Col key={option.id} span={isMobile ? 6 : 4}>
              <UnstyledButton
                onClick={() => onSidekickToggle(option)}
                disabled={
                  option.isRequired ? false : !option.isAvailable && !selectedSidekicks.some(s => s.id === option.id)
                }
                style={{
                  position: 'relative',
                  width: '100%',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #eee',
                  transition: 'all 0.2s ease',
                  backgroundColor: isSelected ? '#f8f9fa' : 'transparent',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                  },
                  opacity: option.isAvailable || isSelected ? 1 : 0.5,
                  cursor: option.isAvailable || isSelected ? 'pointer' : 'not-allowed',
                }}>
                <Group>
                  {option.isRequired ? (
                    <Tooltip label="This is a required user sidekick option" position="top" withArrow>
                      <div
                        style={{
                          position: 'relative',
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          backgroundColor: isSelected ? '#40c057' : 'transparent',
                          border: '2px solid',
                          borderColor: isSelected ? '#40c057' : '#dee2e6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: isSelected ? 'white' : 'transparent',
                          marginRight: '0px',
                        }}>
                        {isSelected && option.isRequired ? <IconLock size={16} /> : <IconCheck size={16} />}
                      </div>
                    </Tooltip>
                  ) : (
                    <div
                      style={{
                        position: 'relative',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: isSelected ? '#40c057' : 'transparent',
                        border: '2px solid',
                        borderColor: isSelected ? '#40c057' : '#dee2e6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isSelected ? 'white' : 'transparent',
                        marginRight: '0px',
                      }}>
                      <IconCheck size={16} />
                    </div>
                  )}
                  <div style={{ position: 'relative' }}>
                    <img
                      src={avatars[option.avatarId] ?? avatars.placeholder}
                      alt={option.name}
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>
                      {option.name}
                    </Text>
                    {isDefault && (
                      <Text c="dimmed" size="xs">
                        (Default)
                      </Text>
                    )}
                    {!option.isAvailable && !isSelected && (
                      <Text c="dimmed" size="xs">
                        (Already assigned)
                      </Text>
                    )}
                  </Stack>

                  {/* Default star icon - top right.
                      NOTE: this lives inside the parent UnstyledButton, so we cannot make this
                      a real <button> (nested buttons are invalid). Existing pre-existing limitation:
                      this control is not keyboard-accessible. We add a role+label so screen
                      readers can at least announce it; full keyboard support requires lifting
                      this out of the parent button (separate ticket). */}
                  <Box
                    role="button"
                    aria-label={
                      isDefault ? `${option.name} is the default sidekick` : `Set ${option.name} as default sidekick`
                    }
                    aria-pressed={isDefault}
                    onClick={e => onDefaultToggle(option, e)}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      padding: '2px',
                      cursor: 'pointer',
                    }}>
                    {isDefault ? (
                      <IconStarFilled
                        size={32}
                        style={{ color: theme.getThemeColor('indigo.6', mantineTheme) }}
                        aria-hidden="true"
                      />
                    ) : (
                      <IconStarFilled
                        size={32}
                        style={{ stroke: '#dee2e6', fill: 'white', strokeWidth: 1.5 }}
                        aria-hidden="true"
                      />
                    )}
                  </Box>
                </Group>
              </UnstyledButton>
            </Grid.Col>
          )
        })}
      </Grid>
    </>
  )
}
